import { useState } from "react";
import api from "@/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, MapPin, Loader2, Phone, Globe, Star, Download, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const FLAGS = { CH: "🇨🇭", DE: "🇩🇪", AT: "🇦🇹" };

export default function DiscoveryPage() {
    const [query, setQuery] = useState("Reisebüro");
    const [location, setLocation] = useState("Zürich");
    const [country, setCountry] = useState("CH");
    const [language, setLanguage] = useState("DE");
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [nextToken, setNextToken] = useState(null);
    const [importing, setImporting] = useState(false);

    const search = async (append = false) => {
        setLoading(true);
        try {
            const { data } = await api.post("/discovery/search", {
                query, location, country,
                page_token: append ? nextToken : null,
            });
            setResults(append ? [...results, ...data.results] : data.results);
            setNextToken(data.next_page_token || null);
            if (!append) setSelected(new Set());
            if (!data.results.length) toast.info("Keine Ergebnisse für diese Suche.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Suche fehlgeschlagen");
        } finally { setLoading(false); }
    };

    const toggleAll = () => {
        const importable = results.filter((r) => !r.already_imported).map((r) => r.place_id);
        if (selected.size === importable.length) setSelected(new Set());
        else setSelected(new Set(importable));
    };

    const toggleOne = (pid) => {
        const next = new Set(selected);
        if (next.has(pid)) next.delete(pid); else next.add(pid);
        setSelected(next);
    };

    const importSelected = async () => {
        if (selected.size === 0) { toast.error("Bitte mindestens einen Eintrag auswählen"); return; }
        setImporting(true);
        try {
            const places = results.filter((r) => selected.has(r.place_id));
            const { data } = await api.post("/discovery/import", { places, default_language: language });
            toast.success(`${data.created} Kontakte hinzugefügt (${data.skipped} übersprungen, schon vorhanden)`);
            // Mark imported
            setResults(results.map((r) => selected.has(r.place_id) ? { ...r, already_imported: true } : r));
            setSelected(new Set());
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Import fehlgeschlagen");
        } finally { setImporting(false); }
    };

    return (
        <div className="space-y-8 animate-fade-in-up" data-testid="discovery-page">
            <div>
                <div className="text-[11px] uppercase tracking-[0.25em] text-[#007bff] font-bold mb-2">Lead-Generierung</div>
                <h1 className="font-display text-5xl text-[#1a2b48] leading-none">Entdecken.</h1>
                <p className="text-slate-500 mt-3 max-w-xl">
                    Reisebüros automatisch aus Google Maps finden — DSGVO-konform, mit Adresse, Telefon, Website.
                    E-Mails fügen Sie anschließend manuell hinzu.
                </p>
            </div>

            {/* Search form */}
            <Card className="p-6 bg-white border-slate-200 rounded-xl">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="space-y-1.5 md:col-span-1">
                        <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Suchbegriff</Label>
                        <Input data-testid="discovery-query" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Reisebüro" />
                    </div>
                    <div className="space-y-1.5 md:col-span-1">
                        <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Stadt / Region</Label>
                        <Input data-testid="discovery-location" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Zürich" />
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Land</Label>
                        <Select value={country} onValueChange={setCountry}>
                            <SelectTrigger data-testid="discovery-country"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="CH">🇨🇭 Schweiz</SelectItem>
                                <SelectItem value="DE">🇩🇪 Deutschland</SelectItem>
                                <SelectItem value="AT">🇦🇹 Österreich</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Standardsprache</Label>
                        <Select value={language} onValueChange={setLanguage}>
                            <SelectTrigger data-testid="discovery-language"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="DE">DE</SelectItem>
                                <SelectItem value="EN">EN</SelectItem>
                                <SelectItem value="FR">FR</SelectItem>
                                <SelectItem value="IT">IT</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <div className="flex justify-end mt-5">
                    <Button onClick={() => search(false)} disabled={loading} data-testid="discovery-search-btn"
                        className="bg-[#007bff] hover:bg-[#0056b3] h-11 px-6 shadow-md shadow-blue-500/20">
                        {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                        Reisebüros suchen
                    </Button>
                </div>
            </Card>

            {results.length > 0 && (
                <>
                    {/* Bulk actions */}
                    <div className="flex flex-wrap items-center justify-between gap-3 bg-blue-50 border border-blue-100 rounded-xl p-4">
                        <div className="flex items-center gap-3">
                            <Button variant="outline" size="sm" onClick={toggleAll} data-testid="discovery-toggle-all">
                                {selected.size === 0 ? "Alle wählen" : "Auswahl aufheben"}
                            </Button>
                            <span className="text-sm text-slate-600">
                                <strong>{selected.size}</strong> ausgewählt von <strong>{results.length}</strong> Treffern
                            </span>
                        </div>
                        <Button onClick={importSelected} disabled={importing || selected.size === 0}
                            data-testid="discovery-import-btn"
                            className="bg-emerald-600 hover:bg-emerald-700">
                            {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                            {selected.size > 0 ? `${selected.size} als Kontakte speichern` : "Auswählen zum Importieren"}
                        </Button>
                    </div>

                    {/* Info box */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800 flex gap-3">
                        <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <div>
                            Google Places liefert <strong>keine E-Mail-Adressen</strong> (Datenschutz). Wir generieren daher automatisch einen
                            Platzhalter wie <code className="text-[11px] bg-white/60 px-1 rounded">info@reisebüro-domain.ch</code> (aus der Website abgeleitet)
                            oder <code className="text-[11px] bg-white/60 px-1 rounded">pending+...@example.com</code>.
                            <strong> Bitte prüfen Sie im Impressum jeder Website die echte E-Mail-Adresse</strong> und korrigieren Sie sie unter
                            <strong> Kontakte → Bearbeiten</strong>, BEVOR Sie eine Kampagne senden!
                        </div>
                    </div>

                    {/* Results table */}
                    <Card className="bg-white border-slate-200 rounded-xl overflow-hidden">
                        <table className="w-full" data-testid="discovery-results-table">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                                    <th className="text-left py-3 px-4 w-10"></th>
                                    <th className="text-left py-3 px-4">Reisebüro</th>
                                    <th className="text-left py-3 px-4">Adresse</th>
                                    <th className="text-left py-3 px-4">Kontakt</th>
                                    <th className="text-left py-3 px-4">Bewertung</th>
                                    <th className="text-left py-3 px-4">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {results.map((r) => (
                                    <tr key={r.place_id} className={`hover:bg-slate-50/40 transition-colors ${r.already_imported ? "opacity-50" : ""}`}
                                        data-testid={`discovery-row-${r.place_id}`}>
                                        <td className="py-3 px-4">
                                            <Checkbox
                                                checked={selected.has(r.place_id)}
                                                disabled={r.already_imported}
                                                onCheckedChange={() => toggleOne(r.place_id)}
                                                data-testid={`discovery-check-${r.place_id}`}
                                            />
                                        </td>
                                        <td className="py-3 px-4">
                                            <div className="font-medium text-[#1a2b48]">{r.name}</div>
                                            {r.website && <a href={r.website} target="_blank" rel="noopener noreferrer"
                                                className="text-xs text-[#007bff] hover:underline flex items-center gap-1 mt-0.5">
                                                <Globe className="w-3 h-3" />{r.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                                            </a>}
                                        </td>
                                        <td className="py-3 px-4 text-sm text-slate-600">
                                            <div className="flex items-start gap-1.5">
                                                <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-slate-400" />
                                                <span className="text-xs">{r.address}</span>
                                            </div>
                                        </td>
                                        <td className="py-3 px-4 text-sm">
                                            {r.phone && <div className="flex items-center gap-1 text-slate-600">
                                                <Phone className="w-3 h-3 text-slate-400" />
                                                <span className="text-xs">{r.phone}</span>
                                            </div>}
                                        </td>
                                        <td className="py-3 px-4">
                                            {r.rating && (
                                                <div className="flex items-center gap-1 text-sm">
                                                    <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                                                    <span className="font-semibold">{r.rating.toFixed(1)}</span>
                                                    <span className="text-xs text-slate-400">({r.ratings_count})</span>
                                                </div>
                                            )}
                                        </td>
                                        <td className="py-3 px-4">
                                            {r.already_imported ? (
                                                <Badge className="bg-slate-200 text-slate-600 border-0 gap-1">
                                                    <CheckCircle2 className="w-3 h-3" />Importiert
                                                </Badge>
                                            ) : (
                                                <Badge className="bg-emerald-100 text-emerald-700 border-0">Neu</Badge>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Card>

                    {nextToken && (
                        <div className="flex justify-center">
                            <Button variant="outline" onClick={() => search(true)} disabled={loading} data-testid="discovery-load-more">
                                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                                Weitere 20 Treffer laden
                            </Button>
                        </div>
                    )}
                </>
            )}

            {!loading && results.length === 0 && (
                <Card className="p-12 text-center bg-white border-slate-200 rounded-xl">
                    <Search className="w-12 h-12 text-slate-200 mx-auto mb-4" strokeWidth={1.3} />
                    <h3 className="font-display text-2xl text-[#1a2b48]">Bereit zur Suche.</h3>
                    <p className="text-slate-500 mt-2 text-sm max-w-md mx-auto">
                        Geben Sie eine Stadt ein (z.B. <em>Zürich</em>, <em>Berlin</em>, <em>Wien</em>) und klicken Sie auf "Reisebüros suchen".
                    </p>
                </Card>
            )}
        </div>
    );
}
